import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { IntegrationsService } from 'src/providers/IntegrationsProvider/Integrations.service';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';

/**
 * Discrete phase of the live WhatsApp Web connection check for the
 * currently-open chat.
 *
 * - `idle`    — the open chat is not in the WhatsApp Web view; the hook is
 *               inert and never fires a fetch.
 * - `pending` — a `getAccountStatus` call is in flight.
 * - `present` — the workspace HAS a WhatsApp Web account (`hasAccount: true`),
 *               whether the account is live or temporarily disconnected.
 * - `absent`  — the workspace has NO WhatsApp Web account (`hasAccount: false`
 *               or an HTTP 404). This is the INT-941 read-only trigger.
 * - `error`   — the live fetch failed (5xx / network) and the answer is
 *               unknown; consumers degrade to the cached IntegrationsProvider
 *               hint (see U2/U3).
 */
export type tWhatsAppWebConnectionPhase =
  | 'idle'
  | 'pending'
  | 'present'
  | 'absent'
  | 'error';

/**
 * Whether the WhatsApp Web channel is read-only — no account to send through.
 *
 * The single source of truth for "what a connection phase means" for the
 * composer (U2) and the pre-send guard (U3). Keeping it here, next to the
 * phase type, keeps the two consumers from drifting apart.
 *
 * `absent` -> read-only outright. On `pending` / `error` / `idle` the live
 * answer is unknown, so it degrades to the cached `IntegrationsProvider` hint
 * (`hasWaWebAccount`) instead of hard fail-closed — a transient blip on a
 * healthy pure-WhatsApp-Web workspace must not lock it out of its only
 * channel. `idle` is reachable only via the pre-send guard (U3), which keys on
 * the resolved channel and can see `idle` paired with a `WHATSAPP_WEB` send
 * when the BUG-336 fallback resolved a WAPI request to WhatsApp Web; degrading
 * to cache there closes a fail-open seam. `present` is never read-only.
 *
 * Since BUG-896 the caller feeds the probe through `resolveProbeIntegration`,
 * so on the WhatsApp Web send path `phase` no longer reaches `idle` by
 * accident: `idle` now means "no chat is open, or the view is not WhatsApp
 * Web". The `idle` branch below is NOT dead code — it stays as the last-resort
 * fail-closed rung for the WAPI-downgrade path, which is why the block is now
 * driven by probe evidence rather than by a stale cache.
 *
 * @param phase            the live `getAccountStatus` phase from this hook.
 * @param hasWaWebAccount  the cached `IntegrationsProvider.hasWaWebAccount()`
 *                         result — the degrade-to-cache fallback.
 */
export function isWhatsAppWebChannelReadOnly(
  phase: tWhatsAppWebConnectionPhase,
  hasWaWebAccount: boolean,
): boolean {
  return (
    phase === 'absent' ||
    ((phase === 'pending' || phase === 'error' || phase === 'idle') &&
      !hasWaWebAccount)
  );
}

type tResolveProbeIntegrationParams = {
  /** `true` only when a chat is open (`selectedLeadForChatModal != null`). */
  hasOpenChat: boolean;
  /** The RAW `selectedLeadForChatModal.chat?.integration` field. */
  requestedIntegration: eChannelType | undefined;
  /**
   * The send path's own default for a lead with no channel yet —
   * `canUserAccessWhatsAppApi() ? WHATSAPP_API : WHATSAPP_WEB`. Passed in
   * rather than recomputed so this stays a pure function of its inputs.
   */
  defaultChannel: eChannelType;
  isWapiConnected: boolean;
};

/**
 * The channel the probe must ask about — the channel the SEND would actually
 * resolve to (BUG-896).
 *
 * `Chat.hook.ts` used to hand the probe the raw `chat?.integration` field while
 * the send path applies `|| defaultChannel` plus the BUG-336 fallback on top of
 * it. Wherever those two disagreed the probe went inert, and the pre-send guard
 * was left deciding on `phase: 'idle'` — degrading to an `IntegrationsProvider`
 * cache that has no refetch-on-focus and can be a day stale.
 *
 * @param params see `tResolveProbeIntegrationParams`.
 * @returns the channel to probe, or `undefined` to keep the hook inert.
 */
export function resolveProbeIntegration(
  params: tResolveProbeIntegrationParams,
): eChannelType | undefined {
  const { hasOpenChat, requestedIntegration, defaultChannel, isWapiConnected } =
    params;

  // With no chat open the hook must stay inert. `Chat` is mounted permanently
  // in Embudo and Directorio — react-daisyui's `Modal.Legacy` always renders
  // its children and only toggles the `modal-open` class — so without this gate
  // the probe would fire on every page load and on every reconnection
  // `catchUpEvent`.
  if (!hasOpenChat) return undefined;

  // Freshly-created lead: `funnelLeadParser` yields `chat: null` and `getLead`
  // returns no `chat` object, so the field is nullish. Default it exactly the
  // way the send does (`|| defaultChannel`, Chat.hook.ts:445-448).
  //
  // A bare `?? WHATSAPP_WEB` is NOT enough, in both directions: it leaves the
  // WAPI-down case below inert (where the send does resolve to WhatsApp Web),
  // and on a WAPI-capable workspace with WAPI live it probes WhatsApp Web for a
  // send that goes out over WhatsApp Business — a `/state` call, 404 on any
  // workspace with no WhatsApp Web account, whose answer nothing reads.
  const channel = requestedIntegration ?? defaultChannel;

  // BUG-336 fallback: a lead still tagged WAPI with the WABA gone sends over
  // WhatsApp Web, so the probe has to ask about WhatsApp Web or the guard is
  // left deciding on `idle`. Applied AFTER the default above, which is what
  // makes `defaultChannel` safe to use here: on its own it would leave the
  // probe inert precisely on the WAPI-capable workspaces that fall back to
  // WhatsApp Web, and this branch is what rescues them.
  if (channel === eChannelType.WHATSAPP_API && !isWapiConnected) {
    return eChannelType.WHATSAPP_WEB;
  }

  return channel;
}

/**
 * Live WhatsApp Web connection status for the open chat. Exposed on
 * `ChatContext` and consumed by the read-only composer (U2) and the
 * pre-send guard (U3).
 */
export type tWhatsAppWebConnectionStatus = {
  phase: tWhatsAppWebConnectionPhase;
  /** Manually re-run the live check (used by U2's read-only banner retry). */
  refetch: () => void;
};

type tUseWhatsAppWebConnectionParams = {
  workspaceId: string | undefined;
  /**
   * The **requested** integration of the open chat —
   * `selectedLeadForChatModal?.chat?.integration`, the field the channel
   * dropdown mutates. NOT the resolved `selectedChat.integration`: the
   * BUG-336 fallback in `Chat.hook.ts` can resolve a WAPI request to
   * `WHATSAPP_WEB` on a migrated workspace, so a resolved-value key would
   * never re-fire on a Web -> Business -> Web toggle and R7 reactivity
   * would be silently lost.
   */
  requestedIntegration: eChannelType | undefined;
};

/**
 * Provides a live, non-cacheable answer to "does this workspace have a
 * WhatsApp Web account?" for the currently-open chat (INT-941 / U1).
 *
 * The `IntegrationsProvider` cache cannot answer this reliably: it has no
 * refetch-on-focus mechanism, so a browser tab opened before a WAPI
 * migration holds stale `CONNECTED` state indefinitely. This hook hits
 * `whatsapp-web-service` directly via `IntegrationsService.getAccountStatus`.
 *
 * Reactive to mid-session changes: it re-runs the fetch on the
 * `workspaceAccountUpdateEvent` and `catchUpEvent` socket events and on a
 * channel toggle into the WhatsApp Web view. For non-WhatsApp-Web views the
 * hook is inert (`phase: 'idle'`, no fetch fired).
 */
export function useWhatsAppWebConnection(
  params: tUseWhatsAppWebConnectionParams,
): tWhatsAppWebConnectionStatus {
  const { workspaceId, requestedIntegration } = params;
  const { workspaceAccountUpdateEvent, catchUpEvent } = useSocketsContext();

  const [phase, setPhase] = useState<tWhatsAppWebConnectionPhase>('idle');

  // Latest-value mirror of the inputs, refreshed every render. An in-flight
  // fetch holds its own captured values across an await; comparing those
  // against these refs detects a chat-switch / channel-toggle that happened
  // mid-flight so a stale response cannot overwrite the now-open chat's
  // status (pattern per Integrations.hook.ts fetchIntegrations).
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;
  const requestedIntegrationRef = useRef(requestedIntegration);
  requestedIntegrationRef.current = requestedIntegration;

  // Monotonic token for the latest dispatched fetch. Two fetches for the SAME
  // chat (e.g. the mount fetch overlapping a socket-triggered re-fetch) share
  // the workspace / integration refs, so the stale-by-input guards below
  // cannot tell them apart; this sequence check ensures only the most-recent
  // dispatch can commit a phase if responses arrive out of order.
  const fetchSeqRef = useRef(0);

  const isWhatsAppWebView = requestedIntegration === eChannelType.WHATSAPP_WEB;

  const fetchStatus = useCallback(async (): Promise<void> => {
    // Capture the inputs before the first await so the response can be
    // matched against the refs afterwards.
    const callWorkspaceId = workspaceIdRef.current;
    const callIntegration = requestedIntegrationRef.current;

    // Inert for non-WhatsApp-Web views: never fire a fetch.
    if (callIntegration !== eChannelType.WHATSAPP_WEB || !callWorkspaceId) {
      return;
    }

    // Claim the latest-dispatch token; only this call may commit a phase.
    const seq = ++fetchSeqRef.current;

    setPhase('pending');

    try {
      const response = await IntegrationsService.getAccountStatus({
        workspaceId: callWorkspaceId,
      });

      // A chat-switch / channel-toggle during the await, or a newer fetch
      // dispatched for the same chat, makes this response stale — drop it so
      // it cannot overwrite the now-open chat's status.
      if (
        callWorkspaceId !== workspaceIdRef.current ||
        callIntegration !== requestedIntegrationRef.current ||
        seq !== fetchSeqRef.current
      ) {
        return;
      }

      // Status fields are nested under `.data` (mirrors ConnectionModal.hook).
      const { data } = response;
      if (!data) {
        LLog.error(
          {
            component: LOG_COMPONENTS.CHAT,
            method: 'useWhatsAppWebConnection.fetchStatus',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Estado inválido en la respuesta de getAccountStatus',
          new Error('Invalid account status response'),
          { responseData: data },
        );
        setPhase('error');
        return;
      }

      setPhase(data.hasAccount ? 'present' : 'absent');
    } catch (error) {
      // A chat-switch / channel-toggle during the await, or a newer fetch for
      // the same chat, makes this rejection stale too — drop it.
      if (
        callWorkspaceId !== workspaceIdRef.current ||
        callIntegration !== requestedIntegrationRef.current ||
        seq !== fetchSeqRef.current
      ) {
        return;
      }

      // A no-account workspace normally returns HTTP 200 `hasAccount: false`
      // (handled above), but the endpoint's exception filter — and ChatDaddy's
      // 204-mask on a deleted account — can surface it as an HTTP 404 instead.
      // Treat 404 as `absent`; distinguish it from a 5xx / network failure ->
      // `error` by inspecting the rejected error's HTTP status.
      const httpStatus = isAxiosError(error)
        ? error.response?.status
        : undefined;

      if (httpStatus === 404) {
        setPhase('absent');
        return;
      }

      LLog.error(
        {
          component: LOG_COMPONENTS.CHAT,
          method: 'useWhatsAppWebConnection.fetchStatus',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al obtener el estado de WhatsApp Web',
        error as unknown as Error,
      );
      setPhase('error');
    }
  }, []);

  // Fire on chat open and on a channel toggle into the WhatsApp Web view.
  // Keyed on the REQUESTED integration (the field the dropdown mutates) plus
  // the workspace id — NOT the chat id and NOT the resolved integration.
  useEffect(() => {
    if (!isWhatsAppWebView || !workspaceId) {
      // Non-WhatsApp-Web view (or no workspace): the hook is inert.
      setPhase('idle');
      return;
    }

    fetchStatus();
  }, [workspaceId, isWhatsAppWebView, fetchStatus]);

  // Re-run the fresh fetch on socket account-update / catch-up events so the
  // status reacts to mid-session connection changes (R7). Subscribed to
  // SocketsContext directly — IntegrationsProvider does not re-broadcast
  // these. `workspaceAccountUpdateEvent` is a connected/disconnected signal
  // only; it serves purely as a re-fetch trigger here, and the live
  // getAccountStatus re-fetch is what actually detects an account deletion.
  // `catchUpEvent` preserves the INT-938 tab-resume self-heal.
  workspaceAccountUpdateEvent.useSubscription(() => {
    if (requestedIntegrationRef.current === eChannelType.WHATSAPP_WEB) {
      fetchStatus();
    }
  });

  catchUpEvent.useSubscription(() => {
    if (requestedIntegrationRef.current === eChannelType.WHATSAPP_WEB) {
      fetchStatus();
    }
  });

  return { phase, refetch: fetchStatus };
}
